/**
 * General-purpose outlier detection service for lab values
 * Uses multiple statistical methods to identify anomalous values
 */

export interface DataPoint {
  date: string;
  value: number;
  units: string;
  id?: number;
  reportId?: number;
  status?: string;
  referenceMin?: number | null;
  referenceMax?: number | null;
  [key: string]: any; // Allow additional properties
}

export interface OutlierResult {
  value: number;
  isOutlier: boolean;
  confidence: 'low' | 'medium' | 'high';
  reason?: string;
  suggestedValue?: number;
  severity?: 'mild' | 'moderate' | 'extreme';
}

export interface EnhancedDataPoint extends DataPoint {
  isOutlier: boolean;
  outlierConfidence?: 'low' | 'medium' | 'high';
  outlierReason?: string;
  displayValue?: number;
  originalValue?: number;
  // Preserve all original properties from DataPoint
  status?: string;
  referenceMin?: number | null;
  referenceMax?: number | null;
  id?: number;
  reportId?: number;
}

/**
 * Calculate percentile of a sorted array
 */
function percentile(sortedValues: number[], p: number): number {
  if (sortedValues.length === 0) return 0;
  const index = (p / 100) * (sortedValues.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  const weight = index % 1;
  return sortedValues[lower] * (1 - weight) + sortedValues[upper] * weight;
}

/**
 * Calculate median of values
 */
function calculateMedian(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  return percentile(sorted, 50);
}

/**
 * Calculate Median Absolute Deviation (MAD) - robust measure of variability
 */
function calculateMAD(values: number[]): number {
  const median = calculateMedian(values);
  const deviations = values.map(v => Math.abs(v - median));
  return calculateMedian(deviations);
}

/**
 * Interquartile Range (IQR) method - robust against extreme outliers
 */
export function detectOutliersIQR(values: number[], multiplier: number = 3.0): OutlierResult[] {
  if (values.length < 4) {
    // Not enough data for IQR
    return values.map(value => ({
      value,
      isOutlier: false,
      confidence: 'low'
    }));
  }

  const sorted = [...values].sort((a, b) => a - b);
  const q1 = percentile(sorted, 25);
  const q3 = percentile(sorted, 75);
  const iqr = q3 - q1;
  
  // Use multiplier * IQR for bounds (3.0 for extreme outliers)
  const lowerBound = q1 - multiplier * iqr;
  const upperBound = q3 + multiplier * iqr;
  
  return values.map(value => {
    const isOutlier = value < lowerBound || value > upperBound;
    let severity: 'mild' | 'moderate' | 'extreme' = 'mild';
    
    if (isOutlier) {
      const distance = Math.min(
        Math.abs(value - lowerBound),
        Math.abs(value - upperBound)
      );
      const iqrMultiple = distance / iqr;
      
      if (iqrMultiple > 6) severity = 'extreme';
      else if (iqrMultiple > 4) severity = 'moderate';
    }
    
    return {
      value,
      isOutlier,
      confidence: isOutlier ? 'high' : 'low',
      severity,
      reason: isOutlier ? 
        `Value is ${value < lowerBound ? 'below' : 'above'} expected range (${lowerBound.toFixed(2)}-${upperBound.toFixed(2)})` : 
        undefined
    };
  });
}

/**
 * Context-aware detection using local temporal patterns
 */
export function detectContextualOutliers(dataPoints: DataPoint[], windowDays: number = 90): OutlierResult[] {
  const results: OutlierResult[] = [];
  
  for (let i = 0; i < dataPoints.length; i++) {
    const current = dataPoints[i];
    const currentDate = new Date(current.date);
    
    // Get values within the context window
    const contextValues = dataPoints
      .filter((dp, idx) => {
        if (idx === i) return false;
        const dpDate = new Date(dp.date);
        const daysDiff = Math.abs(
          (currentDate.getTime() - dpDate.getTime()) / (1000 * 60 * 60 * 24)
        );
        return daysDiff <= windowDays;
      })
      .map(dp => dp.value);
    
    if (contextValues.length < 2) {
      // Not enough context
      results.push({
        value: current.value,
        isOutlier: false,
        confidence: 'low'
      });
      continue;
    }
    
    const median = calculateMedian(contextValues);
    const mad = calculateMAD(contextValues);
    
    // Robust Z-score using MAD
    const robustZScore = mad > 0 ? Math.abs((current.value - median) / (1.4826 * mad)) : 0;
    
    // Determine if outlier based on robust Z-score
    const isOutlier = robustZScore > 3.5;
    
    results.push({
      value: current.value,
      isOutlier,
      confidence: contextValues.length >= 5 ? 'high' : 'medium',
      suggestedValue: median,
      reason: isOutlier ? 
        `Value deviates significantly from recent measurements (median: ${median.toFixed(2)})` : 
        undefined
    });
  }
  
  return results;
}

/**
 * Helper function to check if zero is valid for a test
 */
function isZeroValidForTest(testName: string): boolean {
  const validZeroTests = [
    'BASOPHIL', 'EOSINOPHIL', 'RETICULOCYTE', 'RETIC',
    'IGE', 'IGG', 'IGM', 'IGA', 'ANTIBODY', 'TITER',
    'CEA', 'AFP', 'CA125', 'CA19-9', 'PSA',
    'ETHANOL', 'ALCOHOL', 'DRUG', 'COCAINE', 'MARIJUANA', 'THC',
    'HCG', 'BHCG', 'PROGESTERONE', 'ESTRADIOL'
  ];
  
  const testUpper = testName.toUpperCase();
  return validZeroTests.some(validTest => 
    testUpper.includes(validTest) || validTest.includes(testUpper)
  );
}

/**
 * Rate of change detection - identifies biologically implausible changes
 */
export function detectRateOfChangeOutliers(dataPoints: DataPoint[], testName?: string): OutlierResult[] {
  if (dataPoints.length < 2) {
    return dataPoints.map(dp => ({
      value: dp.value,
      isOutlier: false,
      confidence: 'low'
    }));
  }

  const results: OutlierResult[] = [];
  
  // First point has no previous to compare
  results.push({
    value: dataPoints[0].value,
    isOutlier: false,
    confidence: 'low'
  });
  
  for (let i = 1; i < dataPoints.length; i++) {
    const current = dataPoints[i];
    const previous = dataPoints[i - 1];
    
    // Check if zero/negative values are valid for this test
    const isValidZero = testName && current.value === 0 && isZeroValidForTest(testName);
    
    // Skip if either value is 0 or negative (unless zero is valid for this test)
    if (current.value < 0 || (current.value === 0 && !isValidZero) || previous.value <= 0) {
      results.push({
        value: current.value,
        isOutlier: current.value < 0 || (current.value === 0 && !isValidZero),
        confidence: 'high',
        reason: current.value < 0 ? 'Negative values are errors' : 
                (current.value === 0 && !isValidZero) ? 'Zero values are typically errors for this test' : 
                undefined
      });
      continue;
    }
    
    const daysBetween = Math.abs(
      (new Date(current.date).getTime() - new Date(previous.date).getTime()) / 
      (1000 * 60 * 60 * 24)
    );
    
    // Skip if same day (likely duplicate)
    if (daysBetween < 1) {
      results.push({
        value: current.value,
        isOutlier: false,
        confidence: 'low'
      });
      continue;
    }
    
    // Calculate change ratio
    const changeRatio = current.value / previous.value;
    
    // Normalize to 30-day change rate
    const normalizedChangeRate = Math.pow(changeRatio, 30 / daysBetween);
    
    // Most biomarkers shouldn't change more than 3x or less than 1/3x in 30 days
    const isOutlier = normalizedChangeRate > 3 || normalizedChangeRate < 0.33;
    
    results.push({
      value: current.value,
      isOutlier,
      confidence: daysBetween <= 7 ? 'high' : 'medium',
      reason: isOutlier ? 
        `Value changed ${changeRatio > 1 ? changeRatio.toFixed(1) : (1/changeRatio).toFixed(1)}x in ${Math.round(daysBetween)} days` : 
        undefined
    });
  }
  
  return results;
}

/**
 * Detect extreme statistical outliers (values way outside the distribution)
 */
function detectExtremeOutliers(values: number[]): OutlierResult[] {
  if (values.length < 5) {
    return values.map(value => ({
      value,
      isOutlier: false,
      confidence: 'low'
    }));
  }

  const sorted = [...values].sort((a, b) => a - b);
  const p10 = percentile(sorted, 10);
  const p90 = percentile(sorted, 90);
  
  return values.map(value => {
    // Flag if value is 10x outside the 10-90 percentile range
    const isExtreme = value > p90 * 10 || (p10 > 0 && value < p10 / 10);
    
    return {
      value,
      isOutlier: isExtreme,
      confidence: 'high',
      severity: 'extreme' as const,
      reason: isExtreme ? 
        `Value is ${value > p90 ? 'extremely high' : 'extremely low'} compared to other measurements` : 
        undefined
    };
  });
}

/**
 * Main outlier detection function combining multiple methods
 */
export function detectOutliers(
  testName: string,
  dataPoints: DataPoint[],
  options: {
    sensitivity?: 'low' | 'medium' | 'high';
    maxDisplayMultiplier?: number;
    verifiedDates?: Set<string>;
  } = {}
): EnhancedDataPoint[] {
  const { sensitivity = 'medium', maxDisplayMultiplier = 5, verifiedDates } = options;
  
  if (dataPoints.length === 0) return [];
  
  // Sort by date for rate of change analysis
  const sortedPoints = [...dataPoints].sort((a, b) => 
    new Date(a.date).getTime() - new Date(b.date).getTime()
  );
  
  const values = sortedPoints.map(dp => dp.value);
  
  // Run detection methods
  const iqrResults = detectOutliersIQR(values, sensitivity === 'high' ? 2.5 : 3.0);
  const contextResults = detectContextualOutliers(sortedPoints);
  const rateResults = detectRateOfChangeOutliers(sortedPoints, testName);
  const extremeResults = detectExtremeOutliers(values);
  
  // Calculate reasonable display bounds for chart scaling
  const validValues = values.filter((v, i) => !extremeResults[i].isOutlier);
  const displayMax = validValues.length > 0 ? 
    Math.max(...validValues) * maxDisplayMultiplier : 
    Math.max(...values);
  const displayMin = Math.min(...validValues.filter(v => v > 0));
  
  // Combine results
  return sortedPoints.map((dp, index) => {
    // Skip outlier detection for verified values
    if (verifiedDates && verifiedDates.has(dp.date)) {
      return {
        ...dp,
        isOutlier: false,
        outlierConfidence: undefined,
        outlierReason: undefined,
        displayValue: undefined,
        originalValue: dp.value
      };
    }
    
    const iqr = iqrResults[index];
    const context = contextResults[index];
    const rate = rateResults[index];
    const extreme = extremeResults[index];
    
    // Count outlier votes
    const outlierVotes = [
      iqr?.isOutlier,
      context?.isOutlier,
      rate?.isOutlier,
      extreme?.isOutlier
    ].filter(Boolean).length;
    
    // Determine if outlier based on sensitivity
    let isOutlier = false;
    let confidence: 'low' | 'medium' | 'high' = 'low';
    
    if (extreme?.isOutlier || (dp.value < 0) || (dp.value === 0 && testName && !isZeroValidForTest(testName))) {
      // Always flag extreme outliers and invalid zero/negative values
      isOutlier = true;
      confidence = 'high';
    } else if (outlierVotes >= 3) {
      isOutlier = true;
      confidence = 'high';
    } else if (outlierVotes >= 2) {
      isOutlier = true;
      confidence = 'medium';
    } else if (outlierVotes >= 1 && sensitivity === 'high') {
      isOutlier = true;
      confidence = 'low';
    }
    
    // Collect all reasons
    const reasons = [
      iqr?.reason,
      context?.reason,
      rate?.reason,
      extreme?.reason
    ].filter(Boolean);
    
    // Determine display value (cap extreme outliers for better chart scaling)
    let displayValue = dp.value;
    if (isOutlier && confidence === 'high' && dp.value > displayMax) {
      displayValue = displayMax;
    }
    
    return {
      ...dp,
      isOutlier,
      outlierConfidence: isOutlier ? confidence : undefined,
      outlierReason: reasons.join('; '),
      displayValue: displayValue !== dp.value ? displayValue : undefined,
      originalValue: dp.value
    };
  });
}

/**
 * Get reasonable bounds for chart display
 */
export function getChartDisplayBounds(
  dataPoints: EnhancedDataPoint[],
  referenceMin?: number,
  referenceMax?: number
): { min: number; max: number } {
  const nonOutlierValues = dataPoints
    .filter(dp => !dp.isOutlier || dp.outlierConfidence !== 'high')
    .map(dp => dp.value);
  
  if (nonOutlierValues.length === 0) {
    // All values are outliers, use reference range if available
    if (referenceMin !== undefined && referenceMax !== undefined) {
      return { min: referenceMin * 0.8, max: referenceMax * 1.2 };
    }
    // Fallback to all values
    const allValues = dataPoints.map(dp => dp.value);
    return { 
      min: Math.min(...allValues) * 0.9, 
      max: Math.max(...allValues) * 1.1 
    };
  }
  
  const validMin = Math.min(...nonOutlierValues);
  const validMax = Math.max(...nonOutlierValues);
  
  // Include reference range if provided
  const chartMin = referenceMin !== undefined ? 
    Math.min(validMin, referenceMin) * 0.85 : 
    validMin * 0.9;
    
  // Check if referenceMax is 999 (indicating no upper limit)
  const hasNoUpperLimit = referenceMax === 999;
  const chartMax = referenceMax !== undefined && !hasNoUpperLimit ? 
    Math.max(validMax, referenceMax) * 1.15 : 
    validMax * 1.1;
  
  return { min: chartMin, max: chartMax };
}